import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { awbService } from '../../services';

const AwbMaster = () => {
	const [fromAwb, setFromAwb] = useState('');
	const [toAwb, setToAwb] = useState('');
	const [provider, setProvider] = useState('');
	const [countPreview, setCountPreview] = useState(null);
	const [loading, setLoading] = useState(false);
	const [listLoading, setListLoading] = useState(false);
	const [awbs, setAwbs] = useState([]);
	const [page, setPage] = useState(1);
	const [lastPage, setLastPage] = useState(1);
	const [total, setTotal] = useState(0);

	const hasValidToken = () => {
		try {
			const user = JSON.parse(localStorage.getItem('user') || '{}');
			return Boolean(user && user.token);
		} catch {
			return false;
		}
	};

	const loadAwbs = async (p = 1) => {

		try {
			setListLoading(true);
			if (!hasValidToken()) {
				toast.error('Your session has expired. Please log in again.');
				setListLoading(false);
				return;
			}
			const res = await awbService.list({ page: p });
			// Laravel paginator shape: { data: { data: [...], current_page, last_page, total } }
			const paged = res?.data || res; // in case backend returns data directly
			const items = paged?.data || [];
			setAwbs(items);
			setPage(paged?.current_page || p);
			setLastPage(paged?.last_page || 1);
			setTotal(paged?.total || items.length);
		} catch (e) {
			toast.error(e?.message || 'Failed to load AWBs');
		} finally {
			setListLoading(false);
		}
	};

	useEffect(() => {
		if (hasValidToken()) {
			loadAwbs(1);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const samePrefix = (a, b) => {
		const ma = a.match(/^(\D+)(\d+)$/);
		const mb = b.match(/^(\D+)(\d+)$/);
		if (!ma || !mb) return false;
		return ma[1] === mb[1];
	};

	const calcCountPreview = (a, b) => {
		const ma = a.match(/^(\D+)(\d+)$/);
		const mb = b.match(/^(\D+)(\d+)$/);
		if (!ma || !mb) return null;
		const n1 = parseInt(ma[2], 10);
		const n2 = parseInt(mb[2], 10);
		if (n1 > n2) return null;
		return n2 - n1 + 1;
	};

	useEffect(() => {
		const from = fromAwb;
		const to = toAwb || fromAwb;
		if (from && to && samePrefix(from, to)) {
			setCountPreview(calcCountPreview(from, to));
		} else {
			setCountPreview(null);
		}
	}, [fromAwb, toAwb]);

	const handleCreateRange = async (e) => {
		e.preventDefault();
		if (!fromAwb) {
			toast.error('Please enter From AWB');
			return;
		}
		const from = fromAwb.trim();
		const to = (toAwb || fromAwb).trim();
		if (!samePrefix(from, to)) {
			toast.error('From/To prefixes must match (e.g., AWB200...)');
			return;
		}
		const preview = calcCountPreview(from, to);
		if (!preview || preview <= 0) {
			toast.error('Invalid range. Ensure From <= To');
			return;
		}
		try {
			setLoading(true);
			if (!hasValidToken()) {
				toast.error('Your session has expired. Please log in again.');
				setLoading(false);
				return;
			}
			await awbService.createRange({ from, to, provider: provider || undefined });
			toast.success('AWB range added');
			setFromAwb('');
			setToAwb('');
			setCountPreview(null);
			loadAwbs(1);
		} catch (e) {
			toast.error(e?.message || 'Failed to add AWB range');
		} finally {
			setLoading(false);
		}
	};

	// no filter panel

	const hasPrev = page > 1;
	const hasNext = page < lastPage;

	return (
		<div className="container">
			<div className="mb-6">
				<h1 className="page-title">AWB Master</h1>
				<p className="page-subtitle">Add a range of AWB numbers and view existing AWBs.</p>
			</div>

			<div className="mb-6" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
				<form onSubmit={handleCreateRange} className="card">
					<h2 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>Add AWB Range</h2>
					<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
						<div className="form-group">
							<label className="form-label">From AWB</label>
							<input value={fromAwb} onChange={(e) => setFromAwb(e.target.value)} className="form-input" placeholder="AWB2001234" />
						</div>
						<div className="form-group">
							<label className="form-label">To AWB</label>
							<input value={toAwb} onChange={(e) => setToAwb(e.target.value)} className="form-input" placeholder="AWB2002234" />
						</div>
						<div className="form-group">
							<label className="form-label">Provider (optional)</label>
							<input value={provider} onChange={(e) => setProvider(e.target.value)} className="form-input" placeholder="Bluedart / DTDC" />
						</div>
					</div>
					<div style={{ marginTop: '8px', fontSize: '14px', color: '#6b7280' }}>
						{countPreview ? <span>Will add approximately <span style={{ fontWeight: 600, color: '#111827' }}>{countPreview}</span> AWBs</span> : <span>Enter matching prefixes to see count preview</span>}
					</div>
					<div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
						<button disabled={loading} type="submit" className="btn btn-primary">{loading ? 'Adding…' : 'Add Range'}</button>
						<button type="button" onClick={() => { setFromAwb(''); setToAwb(''); setProvider(''); setCountPreview(null); }} className="btn">Clear</button>
					</div>
				</form>

			</div>

			<div className="card">
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
					<h2 style={{ fontSize: '18px', fontWeight: 600, color: '#111827' }}>AWB List</h2>
					<div style={{ fontSize: '14px', color: '#6b7280' }}>Total: {total}</div>
				</div>
				<div>
					{listLoading ? (
						<div className="loading">Loading…</div>
					) : (
						<table className="table">
							<thead>
								<tr>
									<th>AWB</th>
									<th>Provider</th>
									<th>Status</th>
									<th>Created</th>
								</tr>
							</thead>
							<tbody>
								{awbs.map((row) => (
									<tr key={row.id}>
										<td className="px-4 py-2 font-mono text-sm">{row.awb}</td>
										<td className="px-4 py-2 text-sm"><span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-800">{row.provider || '-'}</span></td>
										<td className="px-4 py-2 text-sm">
											<span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
												row.status === 1 
													? 'bg-green-100 text-green-800' 
													: 'bg-red-100 text-red-800'
											}`}>
												{row.status === 1 ? 'Available' : 'Used'}
											</span>
										</td>
										<td className="px-4 py-2 text-sm text-gray-500">{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</td>
									</tr>
								))}
								{awbs.length === 0 && (
									<tr>
										<td colSpan="4" className="px-4 py-8 text-center text-gray-500">No AWBs found</td>
									</tr>
								)}
							</tbody>
						</table>
					)}
				</div>
				<div className="flex items-center justify-end gap-2 mt-4">
					<button disabled={!hasPrev} onClick={() => loadAwbs(page - 1)} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
					<div className="text-sm text-gray-600">Page {page} of {lastPage}</div>
					<button disabled={!hasNext} onClick={() => loadAwbs(page + 1)} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
				</div>
			</div>
		</div>
	);
};

export default AwbMaster;